import { createBrowserRouter, Navigate, Outlet, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import WebsiteThemeWrapper from "./components/WebsiteThemeWrapper";
import PromoBanner from "./components/PromoBanner";

const ChatWidget = lazy(() =>
  import("./components/ChatWidget").catch(() => {
    return { default: () => null };
  })
);

const Index = lazy(() => import("./pages/Index"));
const StatementOfWork = lazy(() => import("./pages/StatementOfWork"));
const BackofficeLogin = lazy(() => import("./pages/BackofficeLogin"));
const Backoffice = lazy(() => import("./pages/Backoffice"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SOWConfirmation = lazy(() => import("./pages/SOWConfirmation"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ViewProposal = lazy(() => import("./pages/ViewProposal"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AddOns = lazy(() => import("./pages/AddOns"));
const About = lazy(() => import("./pages/About"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Customize = lazy(() => import("./pages/Customize"));
const StrategicPlanning = lazy(() => import("./pages/StrategicPlanning"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OnboardingThankYou = lazy(() => import("./pages/OnboardingThankYou"));
const Account = lazy(() => import("./pages/Account"));
const Profile = lazy(() => import("./pages/Profile"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const ViewMSA = lazy(() => import("./pages/ViewMSA"));
const ProjectRequest = lazy(() => import("./pages/ProjectRequest"));
const ViewQuote = lazy(() => import("./pages/ViewQuote"));
const Solutions = lazy(() => import("./pages/Solutions"));
const PlanChangeRequest = lazy(() => import("./pages/PlanChangeRequest"));
const Partner = lazy(() => import("./pages/Partner"));
const Media = lazy(() => import("./pages/Media"));
const DistributorPartner = lazy(() => import("./pages/DistributorPartner"));
const OurPartners = lazy(() => import("./pages/OurPartners"));
const Helpdesk = lazy(() => import("./pages/Helpdesk"));
const WebsiteAnalysis = lazy(() => import("./pages/WebsiteAnalysis"));
const Anniversary = lazy(() => import("./pages/Anniversary"));
const WomensDay = lazy(() => import("./pages/WomensDay"));
const DistributorPortal = lazy(() => import("./pages/DistributorPortal"));
const BusinessDevelopment = lazy(() => import("./pages/BusinessDevelopment"));
const CampaignLandingPage = lazy(() => import("./pages/CampaignLandingPage"));
const Performance = lazy(() => import("./pages/Performance"));
const BillingDashboard = lazy(() => import("./pages/BillingDashboard"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const ProductCatalog = lazy(() => import("./pages/ProductCatalog"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AMDashboard = lazy(() => import("./pages/AMDashboard"));
const SalesRepDashboard = lazy(() => import("./pages/SalesRepDashboard"));

const ViewProposalRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/proposal?id=${id}`} replace />;
};

const ViewMSARedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/msa/${token}`} replace />;
};

const RootLayout = () => {
  return (
    <>
      <PromoBanner />
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
      <ErrorBoundary fallbackTitle="Something went wrong loading this page">
        <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <WebsiteThemeWrapper><Index /></WebsiteThemeWrapper> },
      { path: "/faq", element: <WebsiteThemeWrapper><FAQ /></WebsiteThemeWrapper> },
      { path: "/add-ons", element: <WebsiteThemeWrapper><AddOns /></WebsiteThemeWrapper> },
      { path: "/about", element: <WebsiteThemeWrapper><About /></WebsiteThemeWrapper> },
      { path: "/testimonials", element: <WebsiteThemeWrapper><Testimonials /></WebsiteThemeWrapper> },
      { path: "/strategic-planning", element: <WebsiteThemeWrapper><StrategicPlanning /></WebsiteThemeWrapper> },
      { path: "/customize", element: <WebsiteThemeWrapper><Customize /></WebsiteThemeWrapper> },
      { path: "/statement-of-work", element: <WebsiteThemeWrapper><StatementOfWork /></WebsiteThemeWrapper> },
      { path: "/backoffice/login", element: <BackofficeLogin /> },
      { path: "/backoffice", element: <ErrorBoundary fallbackTitle="Backoffice encountered an error"><Backoffice /></ErrorBoundary> },
      { path: "/backoffice/:tab", element: <ErrorBoundary fallbackTitle="Backoffice encountered an error"><Backoffice /></ErrorBoundary> },
      { path: "/backoffice/:tab/:projectSlug", element: <ErrorBoundary fallbackTitle="Backoffice encountered an error"><Backoffice /></ErrorBoundary> },
      { path: "/backoffice/reset-password", element: <ResetPassword /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "/sow-confirmation", element: <WebsiteThemeWrapper><SOWConfirmation /></WebsiteThemeWrapper> },
      { path: "/proposal", element: <WebsiteThemeWrapper><ViewProposal /></WebsiteThemeWrapper> },
      { path: "/onboarding", element: <WebsiteThemeWrapper><ErrorBoundary fallbackTitle="Onboarding encountered an error"><Onboarding /></ErrorBoundary></WebsiteThemeWrapper> },
      { path: "/onboarding/thank-you", element: <WebsiteThemeWrapper><OnboardingThankYou /></WebsiteThemeWrapper> },
      { path: "/account", element: <WebsiteThemeWrapper><Account /></WebsiteThemeWrapper> },
      { path: "/profile", element: <WebsiteThemeWrapper><Profile /></WebsiteThemeWrapper> },
      { path: "/portal", element: <WebsiteThemeWrapper><ErrorBoundary fallbackTitle="Portal encountered an error"><ClientPortal /></ErrorBoundary></WebsiteThemeWrapper> },
      { path: "/msa/:token", element: <WebsiteThemeWrapper><ViewMSA /></WebsiteThemeWrapper> },
      { path: "/request", element: <WebsiteThemeWrapper><ProjectRequest /></WebsiteThemeWrapper> },
      { path: "/quote/:token", element: <WebsiteThemeWrapper><ViewQuote /></WebsiteThemeWrapper> },
      { path: "/solutions", element: <WebsiteThemeWrapper><Solutions /></WebsiteThemeWrapper> },
      { path: "/pricing", element: <WebsiteThemeWrapper><Pricing /></WebsiteThemeWrapper> },
      { path: "/plan-change", element: <WebsiteThemeWrapper><PlanChangeRequest /></WebsiteThemeWrapper> },
      { path: "/partner", element: <WebsiteThemeWrapper><Partner /></WebsiteThemeWrapper> },
      { path: "/media", element: <WebsiteThemeWrapper><Media /></WebsiteThemeWrapper> },
      { path: "/distributor", element: <WebsiteThemeWrapper><DistributorPartner /></WebsiteThemeWrapper> },
      { path: "/partners", element: <WebsiteThemeWrapper><OurPartners /></WebsiteThemeWrapper> },
      { path: "/helpdesk", element: <ErrorBoundary fallbackTitle="Helpdesk encountered an error"><Helpdesk /></ErrorBoundary> },
      { path: "/distributor-portal", element: <WebsiteThemeWrapper><DistributorPortal /></WebsiteThemeWrapper> },
      { path: "/bizdev", element: <ErrorBoundary fallbackTitle="Business Development encountered an error"><BusinessDevelopment /></ErrorBoundary> },
      { path: "/performance", element: <ErrorBoundary fallbackTitle="Performance encountered an error"><Performance /></ErrorBoundary> },
      { path: "/billing", element: <ErrorBoundary fallbackTitle="Billing encountered an error"><BillingDashboard /></ErrorBoundary> },
      { path: "/c/:slug", element: <WebsiteThemeWrapper><CampaignLandingPage /></WebsiteThemeWrapper> },
      { path: "/analyze", element: <WebsiteThemeWrapper><WebsiteAnalysis /></WebsiteThemeWrapper> },
      { path: "/anniversary", element: <WebsiteThemeWrapper><Anniversary /></WebsiteThemeWrapper> },
      { path: "/womens-day", element: <WebsiteThemeWrapper><WomensDay /></WebsiteThemeWrapper> },
      { path: "/design-refinement", element: <Navigate to="/onboarding" replace /> },
      { path: "/view-proposal/:id", element: <ViewProposalRedirect /> },
      { path: "/view-msa/:token", element: <ViewMSARedirect /> },
      { path: "/admin/api-keys", element: <ApiKeys /> },
      { path: "/admin/product-catalog", element: <ProductCatalog /> },
      { path: "/admin/am-dashboard", element: <ErrorBoundary fallbackTitle="AM Dashboard layout error"><AMDashboard /></ErrorBoundary> },
      { path: "/admin/sales-performance", element: <ErrorBoundary fallbackTitle="Sales Performance layout error"><SalesRepDashboard /></ErrorBoundary> },
      { path: "*", element: <NotFound /> }
    ]
  }
]);
